import { Flex } from "@redotech/arbiter/flex";
import { AddressInfo } from "@redotech/redo-customer-portal-shared/hooks/useAddress/use-address";
import type { VerifyAddress } from "@redotech/redo-customer-portal-shared/hooks/useVerifyAddress/use-verify-address";
import { ReturnAddressType } from "@redotech/redo-customer-portal-shared/navigator";
import { useTranslation } from "@redotech/redo-i18n";
import { Address as AddressType } from "@redotech/redo-return-operations-common/draft-return/draft-return";
import { memo } from "react";
import { Address, AddressOld, AddressValidationStatus } from "./address";

export interface AddressesProps {
  addresses: AddressInfo[];
  onAddressSelected?: (type: AddressInfo["type"], address: AddressType) => void;
  onValidationStatusChanged?: (status: AddressValidationStatus) => void;
  continueDisabled?: boolean;
  skipComparison?: boolean;
  verifyAddress?: VerifyAddress;
}

export function Addresses({
  addresses,
  onAddressSelected,
  onValidationStatusChanged,
  continueDisabled = false,
  skipComparison = false,
  verifyAddress,
}: AddressesProps) {
  return (
    <Flex dir="column" gap="lg">
      {addresses.map((addressInfo) => (
        <Address
          addressInfo={addressInfo}
          continueDisabled={continueDisabled}
          key={addressInfo.type}
          onAddressSelected={onAddressSelected}
          onValidationStatusChanged={onValidationStatusChanged}
          skipComparison={skipComparison}
          verifyAddress={verifyAddress}
        />
      ))}
    </Flex>
  );
}

export const AddressesOld = memo(function Addresses({
  shippingAddress,
  newOrderAddress,
  hasNonGreenReturnItems,
  showNewItems,
  showNewOrderAddress,
  shippingAddressError,
  newOrderAddressError,
  edit,
  direction = "column",
  showBorder = true,
}: {
  shippingAddress: AddressType | null | undefined;
  newOrderAddress: AddressType | null | undefined;
  hasNonGreenReturnItems: boolean;
  showNewItems: boolean;
  showNewOrderAddress: boolean;
  shippingAddressError?: string;
  newOrderAddressError?: string;
  edit?: (type: ReturnAddressType) => void;
  direction?: "column" | "row";
  showBorder?: boolean;
}) {
  const { t } = useTranslation();
  const showNewOrderCard = hasNonGreenReturnItems && showNewOrderAddress;
  const shippingCardError =
    shippingAddressError ??
    (showNewOrderCard ? undefined : newOrderAddressError);
  return (
    <Flex flexDirection={direction} gap="xl">
      {shippingAddress &&
        (hasNonGreenReturnItems || showNewItems || !!shippingCardError) && (
          <AddressOld
            address={shippingAddress}
            edit={edit ? () => edit(ReturnAddressType.SHIPPING) : undefined}
            errorMessage={shippingCardError}
            showBorder={showBorder}
            title={
              hasNonGreenReturnItems
                ? t("Returning items from:")
                : t("Shipping new items to:")
            }
          />
        )}
      {showNewOrderCard && (
        <AddressOld
          address={newOrderAddress ?? undefined}
          edit={edit ? () => edit(ReturnAddressType.NEW_ORDER) : undefined}
          errorMessage={newOrderAddressError}
          showBorder={showBorder}
          title={t("Shipping new items to:")}
        />
      )}
    </Flex>
  );
});
